import { captureException, startSpan } from "@sentry/react";

import { IFolderResponse, IFoldersResponse } from "../../types";
import { doDelete, doGet, doPostJson, doPutJson } from "../core/http";

export const fetchFolderById = async (
    folderId: string
): Promise<IFolderResponse> => {
    const url = `/api/folders/${folderId}`;
    try {
        const response = await startSpan(
            { name: "fetchFolderById" },
            async () => {
                return await doGet(url, true);
            }
        );
        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }
        return response.data;
    } catch (e) {
        captureException(e);
        return { error: "Failed to fetch folder." };
    }
};

export const fetchFoldersByUser = async (): Promise<IFoldersResponse> => {
    const url = "/api/folders";
    try {
        const response = await startSpan(
            { name: "fetchFoldersByUser" },
            async () => {
                return doGet(url, true);
            }
        );
        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }
        return response.data;
    } catch (e) {
        captureException(e);
        return { error: "Failed to fetch folders." };
    }
};

export const createFolder = async (folderData: {
    name: string;
    parentFolderId: string | null;
}): Promise<IFolderResponse> => {
    try {
        const response = await startSpan({ name: "createFolder" }, async () => {
            return await doPostJson("/api/folders", true, folderData);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        // Assuming backend returns the created folder DTO
        return response.data;
    } catch (e: any) {
        captureException(e);
        return { error: "Failed to create folder" };
    }
};

export const updateFolder = async (
    folderId: string,
    folderData: { name: string; description?: string }
): Promise<IFolderResponse> => {
    try {
        const response = await startSpan({ name: "updateFolder" }, async () => {
            return await doPutJson(
                `/api/folders/${folderId}`,
                true,
                folderData
            );
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        // Assuming backend returns the updated folder DTO
        return response.data;
    } catch (e: any) {
        captureException(e);
        return { error: "Failed to update folder" };
    }
};

export const deleteFolder = async (
    folderId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await startSpan({ name: "deleteFolder" }, async () => {
            return await doDelete(`/api/folders/${folderId}`, true);
        });

        if (response.error) {
            captureException(response.error);
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { success: false, error: "Failed to delete folder" };
    }
};

export const moveImagesToFolder = async (
    folderId: string,
    sourceFolderId: string,
    imageIds: string[]
): Promise<{ success: boolean; error?: string }> => {
    try {
        const payload = {
            imageIds: Array.from(imageIds),
            targetFolderId: folderId,
            sourceFolderId: sourceFolderId,
        };

        const response = await startSpan(
            { name: "moveImagesToFolder" },
            async () => {
                return await doPostJson(
                    "/v1/images/folder/move",
                    true,
                    payload
                );
            }
        );

        if (response.error) {
            captureException(response.error);
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { success: false, error: "Failed to move images to folder" };
    }
};

export const addImagesToFolder = async (
    folderId: string,
    imageIds: string[]
): Promise<{ success: boolean; error?: string }> => {
    try {
        const payload = {
            imageIds: Array.from(imageIds),
            targetFolderId: folderId,
        };

        const response = await startSpan(
            { name: "addImagesToFolder" },
            async () => {
                return await doPostJson("/v1/images/folder/add", true, payload);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { success: false, error: "Failed to add images to folder" };
    }
};

export const removeImagesFromFolder = async (
    folderId: string,
    imageIds: string[]
): Promise<{ success: boolean; error?: string }> => {
    try {
        const payload = {
            imageIds: Array.from(imageIds),
            sourceFolderId: folderId,
        };

        const response = await startSpan(
            { name: "removeImagesFromFolder" },
            async () => {
                return await doPostJson(
                    "/v1/images/folder/remove",
                    true,
                    payload
                );
            }
        );

        if (response.error) {
            captureException(response.error);
            return { success: false, error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { success: false, error: "Failed to remove images from folder" };
    }
};

export const saveFolderNote = async (
    content: string,
    folderId?: string
): Promise<{ success?: boolean; error?: string }> => {
    try {
        const url = folderId
            ? `/api/folders/${folderId}/note`
            : "/api/folders/note";
        const response = await doPostJson(url, true, {
            content,
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { error: "Failed to save note" };
    }
};
